import { Link } from "react-router-dom";
import { ArrowRight, FlaskConical, Radio, Search } from "lucide-react";
import { Logo } from "@/components/AppHeader";
import { CardFace } from "@/components/PlayerCard";

const U = (id) => `https://images.unsplash.com/photo-${id}?crop=entropy&cs=srgb&fm=jpg&q=80&w=700`;
const PREVIEW = [
  { name: "Kendall Brooks", sport: "volleyball", position: "Outside Hitter", team: "Summit State", height_in: 73, jersey_number: "12",
    photo: "https://images.pexels.com/photos/34289740/pexels-photo-34289740.jpeg?auto=compress&cs=tinysrgb&w=700", stats: { kills: 412, digs: 266, aces: 31 } },
  { name: "Maya Okafor", sport: "soccer", position: "Forward", team: "Riverside FC", height_in: 67, jersey_number: "9",
    photo: U("1603291697926-7e5822ed1ac5"), stats: { goals: 14, assists: 6, appearances: 18 } },
  { name: "Taylor Grant", sport: "volleyball", position: "Middle Blocker", team: "Summit State", height_in: 76, jersey_number: "15",
    photo: U("1686753767461-35673e3dfa77"), stats: { kills: 298, digs: 64, aces: 12 } },
];

const FEATURES = [
  [Search, "Search every roster", "Filter by sport, position, team, age and height. Find the player you need in seconds."],
  [Radio, "Live season stats", "Soccer and volleyball numbers flow in from FanFlare and refresh every week."],
  [FlaskConical, "Verified testing", "Vertical, sprint and agility results sit right next to the game numbers."],
];

const tilt = ["-rotate-[8deg] translate-y-8", "-translate-y-2 scale-105", "rotate-[8deg] translate-y-8"];

export default function Landing() {
  return (
    <div className="grain relative min-h-screen overflow-hidden bg-[#0B0F19]">
      <header className="relative z-20 mx-auto flex h-20 max-w-[1400px] items-center justify-between px-4 sm:px-8">
        <Logo />
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="landing-login-link" className="px-3 py-2 text-sm text-slate-300 transition-colors hover:text-white">Log in</Link>
          <Link to="/request-access" data-testid="landing-request-invite-header-button"
            className="rounded-full bg-[#FF2D78] px-5 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#E01F66]">Request invite</Link>
        </div>
      </header>

      <section className="relative z-10 mx-auto grid max-w-[1400px] items-center gap-16 px-4 pb-24 pt-10 sm:px-8 lg:grid-cols-[1.1fr_1fr] lg:pt-20">
        <div className="rise">
          <p className="eyebrow mb-6 !text-[#FF2D78]">The women's athlete database</p>
          <h1 className="font-display text-5xl font-black uppercase leading-[0.9] tracking-tight sm:text-6xl">
            Know her name.<br />Know her numbers.
          </h1>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-400">
            Player cards for every soccer, volleyball, softball, basketball and lacrosse athlete — bio, season stats and athletic testing results in one place.
            Built for the coaches, scouts, fans and media who want to know who she is.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/request-access" data-testid="landing-request-invite-button"
              className="group inline-flex items-center gap-2 rounded-full bg-[#FF2D78] px-7 py-3.5 font-semibold text-white transition-colors duration-200 hover:bg-[#E01F66]">
              Request an invitation <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <Link to="/login" data-testid="landing-member-login-button"
              className="rounded-full border border-slate-700 px-7 py-3.5 font-semibold text-slate-200 transition-colors duration-200 hover:border-white">
              Member log in
            </Link>
          </div>
          <div className="mt-12 flex gap-8 font-data text-xs uppercase tracking-widest text-slate-500">
            <span><span className="text-white">05</span> Sports</span>
            <span><span className="text-white">Weekly</span> Stat sync</span>
            <span><span className="text-white">Invite</span> Only</span>
          </div>
        </div>
        <div className="relative mx-auto flex h-[420px] w-full max-w-[560px] items-center justify-center sm:h-[480px]">
          {PREVIEW.map((p, i) => (
            <div key={p.name} style={{ animationDelay: `${150 + i * 120}ms` }} className={`rise -mx-8 shrink-0 ${i === 1 ? "z-10" : ""}`}>
              <div data-testid={`landing-preview-card-${i}`}
                className={`group relative aspect-[3/4] w-40 overflow-hidden rounded-xl border border-slate-700 shadow-[0_30px_60px_rgba(0,0,0,0.6)] transition-transform duration-300 hover:-translate-y-4 sm:w-60 ${tilt[i]}`}>
                <CardFace p={p} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="relative z-10 border-t border-slate-800/80">
        <div className="mx-auto grid max-w-[1400px] md:grid-cols-3">
          {FEATURES.map(([Icon, title, copy], i) => (
            <div key={title} className={`p-8 sm:p-12 ${i ? "border-t border-slate-800/80 md:border-l md:border-t-0" : ""}`}>
              <Icon className="h-6 w-6 text-[#FF2D78]" />
              <h2 className="mt-5 font-display text-2xl font-extrabold uppercase tracking-tight">{title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{copy}</p>
            </div>
          ))}
        </div>
      </section>
      <footer className="relative z-10 border-t border-slate-800/80 px-4 py-8 text-center font-data text-[11px] uppercase tracking-widest text-slate-600">
        © {new Date().getFullYear()} KnowHerName.com
      </footer>
    </div>
  );
}
